/**
 * Demand Gen highlight utilities.
 *
 * Centralises the logic for mapping highlightData / primaryHighlights to cell
 * background styles so every row type uses the same rules.
 *
 * Corner cases handled in every function:
 *   - null / undefined data props  → returns null (no style applied)
 *   - industry / segment not found → returns null
 *   - industryMeetsThresholds: false → returns null (row is greyed, not highlighted)
 *   - all-zero / ties              → backend already returns empty arrays; null returned
 */

/**
 * Returns a { background, color } style object for a per-segment cell, or null
 * when no highlight applies (cell stays at its default colour).
 *
 * @param {Array}  highlightData  – array of per-industry highlight objects from the backend
 * @param {string} primaryKey     – field name used as the industry key (e.g. "Vertical")
 * @param {string} industry       – the primary dimension value for this row
 * @param {string} maxField       – highlight object field containing max segment names (array)
 * @param {string} minField       – highlight object field containing min segment names (array)
 * @param {string} segment        – the secondary dimension value for this cell
 */
export const getSegmentHighlight = (highlightData, primaryKey, industry, maxField, minField, segment) => {
  if (!highlightData) return null
  const data = highlightData.find(i => i[primaryKey] === industry)
  if (!data || data.industryMeetsThresholds === false) return null
  if (Array.isArray(data[maxField]) && data[maxField].includes(segment)) {
    return { background: data.maxBgColor, color: 'black' }
  }
  if (Array.isArray(data[minField]) && data[minField].includes(segment)) {
    return { background: data.minBgColor, color: 'black' }
  }
  return null
}

/**
 * Returns a { background, color } style object for the Grand Total column cell
 * of a primary dimension row (the rightmost "Total" column), or null when no
 * highlight applies.
 *
 * Uses primaryHighlights which compares totals ACROSS primary dimensions, so the
 * shade is intentionally different from per-segment highlights.
 *
 * Eligibility is pre-filtered by the backend (only ACV-threshold-passing primaries
 * are candidates), so the frontend just checks membership.
 *
 * @param {Object} primaryHighlights – cross-primary highlight object from the backend
 * @param {string} maxField          – field containing primary dimension names at the max
 * @param {string} minField          – field containing primary dimension names at the min
 * @param {string} industry          – the primary dimension value for this row
 */
export const getPrimaryHighlight = (primaryHighlights, maxField, minField, industry) => {
  if (!primaryHighlights) return null
  if (Array.isArray(primaryHighlights[maxField]) && primaryHighlights[maxField].includes(industry)) {
    return { background: primaryHighlights.maxBgColor, color: 'white' }
  }
  if (Array.isArray(primaryHighlights[minField]) && primaryHighlights[minField].includes(industry)) {
    return { background: primaryHighlights.minBgColor, color: 'white' }
  }
  return null
}
